import {
	PropsWithChildren,
	ReactElement,
	useEffect,
	useRef,
	useState,
	useCallback,
	ReactNode,
	Children,
	useMemo,
	RefObject,
	ReadonlyRefObject,
	useLayoutEffect
} from "react";
import classNames from "classnames";
import { useForceRerender, useStableImperativeCallback } from "client/Hooks";
import { getReactNodeKey } from "client/Misc/reactUtils";
import { reflow } from "client/Misc/domUtils";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import styles from "./CollapsibleStack.module.less";
import { debounce, ensureNotNil } from "client/Misc/utils";
import { registerSizeListener } from "client_legacy/ndaw/sizing";

type HeightChange = {
	itemKey: string;
	height: number;
};

export type CollapsibleStackStateKind = "collapsed" | "expanded";

type CollapsibleStackProps = {
	state: CollapsibleStackStateKind;
	coverElementIndex?: number;
	coverElementVisualTopOffset?: number;
	itemVisualBottomExpanse?: number;
	itemVisualSideExpanse?: number;
	onAnimationComplete?(): void;
};

export const CollapsibleStack = (props: PropsWithChildren<CollapsibleStackProps>): ReactElement | null => {
	const {
		state,
		coverElementIndex = 0,
		coverElementVisualTopOffset = 0,
		itemVisualBottomExpanse = 0,
		itemVisualSideExpanse = 0,
		onAnimationComplete,
		children
	} = props;

	const debouncedOnAnimationComplete: (() => void)|undefined = useMemo(
		() => {
			return onAnimationComplete && debounce(onAnimationComplete, 50);
		},
		[onAnimationComplete]
	);

	const stackRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const [isAnimationEnabled, setIsAnimationEnabled] = useState<boolean>(false);

	const [measuredHeights, setMeasuredHeights] = useState<Map<string, number>>(new Map());
	const [notifyAboutHeightChanges, immediateHeightChangesVersion] = useForceRerender();
	const unprocessedHeightChanges: ReadonlyRefObject<HeightChange[]> = useRef<HeightChange[]>([]);

	const childrenArray: ReactNode[] = useMemo(
		() => Children.toArray(children),
		[children]
	);

	const childKeys: ReadonlyArray<string> = useMemo(
		() => childrenArray.map((c, i) => getReactNodeKey(c, i)),
		[childrenArray]
	);

	useEffect(
		() => {
			setMeasuredHeights(measuredHeights => {
				const resultMap = new Map<string, number>();

				for (const childKey of childKeys) {
					const currentValue: number | undefined = measuredHeights.get(childKey);
					if (typeof currentValue === "number") {
						resultMap.set(childKey, currentValue);
					}
				}

				const hasNoChanges: boolean = resultMap.size === measuredHeights.size;
				return hasNoChanges
					? measuredHeights
					: resultMap;
			});
		},
		[childKeys]
	);

	const isEverythingMeasured: boolean = measuredHeights.size === childrenArray.length;

	useEffect(
		() => {
			reflow(stackRef.current);
			setIsAnimationEnabled(isEverythingMeasured);
		},
		[isEverythingMeasured]
	);

	const getItemHeight = useCallback(
		(itemIndex: number): number => {
			const key: string = childKeys[itemIndex];
			const immediateResult: HeightChange | undefined = unprocessedHeightChanges.current.find(c => c.itemKey === key);
			if (immediateResult) {
				return immediateResult.height;
			}

			const measuredResult: number|undefined = measuredHeights.get(key);
			if (typeof measuredResult === "number") {
				return measuredResult;
			}

			return 0;
		},
		[childKeys, measuredHeights]
	);

	const coverItemHeight: number = getItemHeight(coverElementIndex);

	const [childrenTops, totalHeight] = useMemo(
		(): [ReadonlyArray<number>, number] => {
			const result: number[] = [];
			result.length = childKeys.length;

			let top: number = 0;

			for (let i = 0; i < childKeys.length; i++) {
				const height: number = getItemHeight(i);

				result[i] = top;

				top += height;
			}

			return [result, top];
		},
		[childKeys, getItemHeight]
	);

	const heightChangedHandler = useCallback(
		(itemKey: string, height: number): void => {
			if (height > 0) {
				unprocessedHeightChanges.current.push({ itemKey, height });
				notifyAboutHeightChanges();
			}
		},
		[notifyAboutHeightChanges]
	);

	useEffect(
		() => {
			if (unprocessedHeightChanges.current.length === 0) {
				return;
			}

			const changesToPersist: HeightChange[] = [...unprocessedHeightChanges.current];
			unprocessedHeightChanges.current.length = 0;

			setMeasuredHeights(measuredHeights => {
				const result = new Map(measuredHeights);

				for (const heightChange of changesToPersist) {
					result.set(heightChange.itemKey, heightChange.height);
				}

				return result;
			});
		},
		[immediateHeightChangesVersion]
	);

	const getItemTop = useCallback(
		(itemIndex: number): number => {
			switch (state) {
				case "collapsed":
					return coverItemHeight - getItemHeight(itemIndex);
				case "expanded":
					return childrenTops[itemIndex];
				default:
					throw new UnreachableCaseError(state);
			}
		},
		[childrenTops, coverItemHeight, getItemHeight, state]
	);

	const getItemZIndex = useCallback(
		(itemIndex: number): number => {
			return childrenArray.length - Math.abs(coverElementIndex - itemIndex);
		},
		[childrenArray.length, coverElementIndex]
	);

	if (childrenArray.length === 0) {
		return null;
	}

	if (childrenArray.length <= coverElementIndex) {
		console.error(`Cannot use child with index ${coverElementIndex} as cover item, because there is only ${childrenArray.length} items`);
	}
	let listHeight: number;

	switch (state) {
		case "collapsed":
			listHeight = coverItemHeight;
			break;
		case "expanded":
			listHeight = totalHeight;
			break;
		default:
			throw new UnreachableCaseError(state);
	}

	return (
		<div
			className={classNames(
				styles.root,
				{ [styles.root_hasTransition]: isAnimationEnabled }
			)}
			style={{height: listHeight}}
			onTransitionEnd={debouncedOnAnimationComplete}
		>
			{childrenArray.map((item, index) => (
				<StackItem
					height={getItemHeight(index)}
					key={childKeys[index]}
					itemKey={childKeys[index]}
					top={getItemTop(index)}
					onHeightChanged={heightChangedHandler}
					zIndex={getItemZIndex(index)}
					topOffset={index === coverElementIndex ? undefined : state === "expanded" ? 0 : coverElementVisualTopOffset}
					bottomExpanse={itemVisualBottomExpanse}
					sideExpanse={itemVisualSideExpanse}
					hasTransition={isAnimationEnabled}
				>
					{item}
				</StackItem>
			))}
		</div>
	);
};


type StackItemProps = {
	top: number;
	height: number;
	zIndex: number;
	itemKey: string;
	topOffset: number | undefined;
	bottomExpanse: number;
	sideExpanse: number;
	hasTransition: boolean;
	onHeightChanged: (itemKey: string, height: number) => void;
};

const StackItem = (props: PropsWithChildren<StackItemProps>): ReactElement => {
	const {
		top,
		height,
		zIndex,
		topOffset,
		bottomExpanse,
		sideExpanse,
		onHeightChanged,
		itemKey,
		hasTransition,
		children
	} = props;

	const measurementContainerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const stableHeightChangeHandler = useStableImperativeCallback(
		(newHeight: number): void => {
			onHeightChanged(itemKey, newHeight);
		}
	);

	useLayoutEffect(
		(): EffectWithDestructorResult => {
			const container: HTMLDivElement = ensureNotNil(measurementContainerRef.current);
			const immediateHeight: number = container.clientHeight;

			stableHeightChangeHandler(immediateHeight);

			return registerSizeListener(container, (size) => stableHeightChangeHandler(size.height));
		},
		[stableHeightChangeHandler]
	);

	const clipPath: string | undefined = typeof topOffset === "number"
		? `inset(${topOffset - top}px ${-sideExpanse}px ${-bottomExpanse}px)`
		: undefined;

	return (
		<div
			className={classNames(
				styles.itemWrapper,
				{ [styles.itemWrapper_hasTransition]: hasTransition }
			)}
			style={{top, height, zIndex, clipPath}}
		>
			<div
				ref={measurementContainerRef}
			>
				{children}
			</div>
		</div>
	);
};

StackItem.displayName = "StackItem";